import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Users, Share2 } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// Visão só-leitura e reduzida (LGPD) do cadastro de kids/teens, pensada pro
// papel SECRETARIA_CONSULTA -- consome /children/directory (não
// /children), que já devolve só nome, cidade/bairro, status e aniversário
// sem o ano. Sem CPF (não existe aqui), sem endereço completo, sem contato
// de responsável, sem saúde (alergia/medicação) e sem anotações internas --
// e sem nenhum botão de editar/excluir/converter. Ver AdminCriancas.jsx
// pra tela completa (papel SECRETARIA/ADMIN).
const STATUS_LABELS = { ATIVO: "Ativo", INATIVO: "Inativo (saiu)", CONVERTIDO: "Virou membro", PENDENTE: "Pendente" };
const STATUS_COLORS = {
  ATIVO: { border: "rgba(74,222,128,0.45)", background: "rgba(34,197,94,0.14)", color: "#86EFAC" },
  INATIVO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.12)", color: "#c7ced9" },
  CONVERTIDO: { border: "rgba(125,211,252,0.4)", background: "rgba(14,165,233,0.12)", color: "#7DD3FC" },
  PENDENTE: { border: "rgba(251,191,36,0.45)", background: "rgba(251,191,36,0.14)", color: "#FDE68A" }
};
// Mesmas opcoes/ordem do filtro de status na tela completa (AdminCriancas.jsx).
const STATUS_OPTIONS = ["ATIVO", "INATIVO", "PENDENTE"];

// Mesmas 3 faixas do filtro de idade da tela completa (AGE_FILTER_LABELS em
// AdminCriancas.jsx) -- aqui o filtro roda sobre child.ageBand, que o
// backend ja calcula e devolve pronto (ver ageBandFromBirthDate em
// mosaico-api/src/routes/children.js): o ano de nascimento nunca chega
// nesta tela, so a faixa.
const AGE_FILTER_LABELS = {
  ATE_8: "Até 8 anos",
  DE_9_A_15: "9 a 15 anos",
  MAIS_16: "16 anos ou mais"
};

const MONTH_NAMES = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez"
];

const MONTH_NAMES_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit"
};

const selectStyle = { ...inputStyle };

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminCriancasConsulta({ portalAccess } = {}) {
  const [status, setStatus] = useState("checking");
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ATIVO");
  const [ageFilter, setAgeFilter] = useState("");
  const [birthdayMonth, setBirthdayMonth] = useState("");
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  useEffect(() => {
    // Quando chega via o portal (AdminMembrosPortal.jsx / AdminCriancasPortal.jsx),
    // o papel de quem esta logado ja foi checado uma vez la -- evita bater
    // /auth/me de novo aqui e reaparecer um "Carregando..." por cima do que
    // o portal ja resolveu.
    if (portalAccess !== undefined) {
      setStatus(portalAccess ? "ready" : "forbidden");
      return undefined;
    }
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const effectiveMeRoles = getEffectiveRoles(me);
        const canAccess =
          effectiveMeRoles.includes("ADMIN") ||
          effectiveMeRoles.includes("SECRETARIA") ||
          effectiveMeRoles.includes("SECRETARIA_CONSULTA");
        if (canAccess && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, [portalAccess]);

  const loadChildren = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const data = await apiFetch(`/children/directory${query}`, { method: "GET" });
      setChildren(Array.isArray(data) ? data : []);
    } catch (error) {
      setChildren([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    loadChildren("");
  }, [status, loadChildren]);

  useEffect(() => {
    if (status !== "ready") return;
    const timeout = setTimeout(() => loadChildren(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const statusFilteredChildren = statusFilter
    ? children.filter((child) => (child.status || "ATIVO") === statusFilter)
    : children;

  const ageFilteredChildren = ageFilter
    ? statusFilteredChildren.filter((child) => child.ageBand === ageFilter)
    : statusFilteredChildren;

  const visibleChildren = birthdayMonth
    ? ageFilteredChildren.filter((child) => child.birthMonth === Number(birthdayMonth))
    : ageFilteredChildren;

  const copyShareLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2500);
    });
  };

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à secretaria (inclusive papel de consulta) e administradores.</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
              Ministério infantil · consulta
            </p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Kids e Teens (consulta)</h1>
          </div>
          <button
            onClick={() => copyShareLink("/cadastro/crianca")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
            title="Copia o link do formulário público de cadastro de criança/adolescente"
          >
            <Share2 size={13} />
            {shareLinkCopied ? "Link copiado!" : "Compartilhar link"}
          </button>
        </div>
        <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
          Visão reduzida do cadastro -- sem contato de responsável, saúde ou anotações internas. Pra editar ou ver o cadastro completo, use um acesso de secretaria.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ position: "relative", maxWidth: 320, flex: "1 1 220px" }}>
            <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#7f8ba3" }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome ou cidade"
              style={{ ...inputStyle, paddingLeft: 36 }}
            />
          </div>
          <div style={{ maxWidth: 200, flex: "1 1 160px" }}>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={selectStyle}
              title="Filtrar por status"
            >
              <option value="ATIVO">Ativos</option>
              <option value="">Todos</option>
              {STATUS_OPTIONS.filter((option) => option !== "ATIVO").map((option) => (
                <option key={option} value={option}>{STATUS_LABELS[option]}</option>
              ))}
            </select>
          </div>
          <div style={{ maxWidth: 220, flex: "1 1 180px" }}>
            <select
              value={birthdayMonth}
              onChange={(event) => setBirthdayMonth(event.target.value)}
              style={selectStyle}
              title="Filtrar aniversariantes do mês"
            >
              <option value="">Aniversariantes -- todos os meses</option>
              {MONTH_NAMES_FULL.map((name, index) => (
                <option key={name} value={String(index + 1)}>Aniversariantes de {name}</option>
              ))}
            </select>
          </div>
          <div style={{ maxWidth: 200, flex: "1 1 170px" }}>
            <select
              value={ageFilter}
              onChange={(event) => setAgeFilter(event.target.value)}
              style={selectStyle}
              title="Filtrar por faixa de idade"
            >
              <option value="">Todas as idades</option>
              <option value="ATE_8">Até 8 anos</option>
              <option value="DE_9_A_15">9 a 15 anos</option>
              <option value="MAIS_16">16 anos ou mais</option>
            </select>
          </div>
        </div>

        <div style={cardStyle}>
          {loading && children.length === 0 && <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>Carregando...</p>}
          {!loading && visibleChildren.length === 0 && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              {birthdayMonth
                ? `Ninguém faz aniversário em ${MONTH_NAMES_FULL[Number(birthdayMonth) - 1]}.`
                : ageFilter
                ? `Ninguém na faixa "${AGE_FILTER_LABELS[ageFilter]}" agora.`
                : search.trim()
                ? `Nenhum registro encontrado para "${search}".`
                : statusFilter
                ? `Nenhum cadastro com o status "${STATUS_LABELS[statusFilter] || statusFilter}" agora.`
                : "Nenhum cadastro encontrado."}
            </p>
          )}
          {visibleChildren.map((child, index) => {
            const statusStyle = STATUS_COLORS[child.status] || STATUS_COLORS.ATIVO;
            return (
              <div
                key={child.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 14,
                  alignItems: "center",
                  padding: "14px 0",
                  borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.08)"
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, color: "#E5E7EB", fontSize: 14, fontWeight: 850 }}>
                    {child.fullName}
                    {child.birthMonth && child.birthDay && (
                      <span style={{ color: "#9fb0cc", fontWeight: 700 }}> · aniversário {String(child.birthDay).padStart(2, "0")}/{MONTH_NAMES[child.birthMonth - 1]}</span>
                    )}
                  </p>
                  <p style={{ margin: "3px 0 0", color: "#9fb0cc", fontSize: 12 }}>
                    {[child.city, child.neighborhood].filter(Boolean).join(" · ") || "Sem cidade/bairro cadastrado"}
                    {child.householdId && (
                      <span style={{ color: "#f0a84e" }}>
                        {" "}· <Users size={11} style={{ verticalAlign: "-2px" }} /> vinculada(o) a uma família
                      </span>
                    )}
                  </p>
                </div>
                <span
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${statusStyle.border}`,
                    background: statusStyle.background,
                    color: statusStyle.color,
                    fontSize: 11,
                    fontWeight: 900,
                    whiteSpace: "nowrap"
                  }}
                >
                  {STATUS_LABELS[child.status] || child.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
